import { useEffect, useState } from 'react'
import { apiClient } from '@/services/api/client'

/** Only fetch visual previews once they enter the viewport; revoke URLs on unmount. */
export function useDocumentThumbnail(caseId: string, documentId: string | undefined, kind: string) {
  const [element, setElement] = useState<HTMLDivElement | null>(null)
  const [visible, setVisible] = useState(false)
  const [url, setUrl] = useState<string | null>(null)
  const [excerpt, setExcerpt] = useState<string | null>(null)

  useEffect(() => {
    if (!element || !documentId || (kind !== 'pdf' && kind !== 'image' && kind !== 'docx')) return
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) setVisible(true)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [element, documentId, kind])

  useEffect(() => {
    if (!visible || !documentId || (kind !== 'pdf' && kind !== 'image' && kind !== 'docx')) return
    const controller = new AbortController()
    let objectUrl: string | null = null
    const load = async () => {
      try {
        const token = apiClient.getToken()
        const response = await fetch(
          `/api/v1/cases/${encodeURIComponent(caseId)}/documents/${encodeURIComponent(documentId)}/download`,
          {
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            signal: controller.signal,
          }
        )
        if (!response.ok) throw new Error(`Preview failed: ${response.status}`)
        const blob = await response.blob()
        if (controller.signal.aborted) return
        if (kind === 'docx') {
          const mammoth = await import('mammoth')
          const result = await mammoth.extractRawText({ arrayBuffer: await blob.arrayBuffer() })
          if (!controller.signal.aborted) setExcerpt(result.value.trim().slice(0, 350))
        } else {
          objectUrl = URL.createObjectURL(
            kind === 'pdf' && blob.type !== 'application/pdf'
              ? new Blob([blob], { type: 'application/pdf' })
              : blob
          )
          setUrl(objectUrl)
        }
      } catch (error) {
        if (!controller.signal.aborted) console.error('Document thumbnail failed:', error)
      }
    }
    void load()
    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [caseId, documentId, kind, visible])

  return { setElement, url, excerpt }
}
